import React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import {
  changelogEntries,
  copyrightText,
  currentVersion,
} from "app/version/versionInfo";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

const Footer = () => {
  const year = moment().format("YYYY");
  // Footer is rendered outside of a Suspense boundary in some layouts.
  // Disable i18n suspense here to avoid runtime crash: "Footer suspended while rendering".
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { getAuthUser } = useJumboAuth();
  const [open, setOpen] = React.useState(false);
  const currentYear = new Date().getFullYear();

  let localUser = null;
  try {
    const raw = localStorage.getItem("user");
    if (raw) localUser = JSON.parse(raw);
  } catch (_e) {
    localUser = null;
  }
  const authUser = getAuthUser?.() || null;
  const finalUser = authUser || localUser || null;

  const resolvedUserIdRaw =
    finalUser?.userId ??
    finalUser?.id ??
    finalUser?._id ??
    finalUser?.user?.id ??
    finalUser?.user?._id ??
    null;
  const resolvedUserId = Number(resolvedUserIdRaw);
  const canSeeChangelog =
    Number.isFinite(resolvedUserId) && resolvedUserId === 1;

  const resolvedCustomerIdRaw =
    finalUser?.customer?.id ??
    finalUser?.customerId ??
    finalUser?.customer_id ??
    null;
  const displayCustomerId =
    resolvedCustomerIdRaw != null ? String(resolvedCustomerIdRaw) : "-";

  React.useEffect(() => {
    if (!canSeeChangelog && open) setOpen(false);
  }, [canSeeChangelog, open]);

  const handleOpenChangelog = () => {
    if (canSeeChangelog) setOpen(true);
  };

  return (
    <Div
      sx={{
        py: 2,
        px: { lg: 6, xs: 4 },
        borderTop: 2,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Div
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant={"body1"}
            color={"text.primary"}
            sx={{ whiteSpace: "nowrap" }}
          >
            Datavision {currentVersion} {copyrightText || `© 2022 - ${year}`} [{" "}
            {displayCustomerId} ]
          </Typography>
          {canSeeChangelog ? (
            <Tooltip
              title={t("footer.changelog.tooltip", {
                defaultValue: "Zobraziť zoznam zmien",
              })}
            >
              <Button
                variant="text"
                size="small"
                onClick={handleOpenChangelog}
                sx={{ textTransform: "none", minWidth: "auto", px: 1 }}
              >
                {t("footer.changelog.label", { defaultValue: "Changelog" })}
              </Button>
            </Tooltip>
          ) : null}
        </Stack>
      </Div>

      {canSeeChangelog ? (
        <Dialog
          open={open && canSeeChangelog}
          onClose={() => setOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pr: 6 }}>
            {t("footer.changelog.title", { defaultValue: "Changelog" })}
            <IconButton
              aria-label={t("footer.changelog.close", {
                defaultValue: "Zatvoriť",
              })}
              onClick={() => setOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t("footer.changelog.version", { defaultValue: "Verzia" })}:{" "}
                {currentVersion}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("footer.changelog.period", { defaultValue: "Obdobie" })}:{" "}
                {t("footer.changelog.since", { defaultValue: "od 1.1." })}{" "}
                {currentYear}
              </Typography>

              {[...changelogEntries].reverse().map((entry, idx) => (
                <Div key={changelogEntries.length - 1 - idx} sx={{ mt: 1 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {entry.section}
                  </Typography>
                  <Div component="ul" sx={{ pl: 3, m: 0 }}>
                    {entry.items.map((it, i) => (
                      <li key={i}>
                        <Typography variant="body2">{it}</Typography>
                      </li>
                    ))}
                  </Div>
                </Div>
              ))}
            </Stack>
          </DialogContent>
        </Dialog>
      ) : null}
    </Div>
  );
};

export default Footer;
